import {combineLatestWith, filter, tap} from 'rxjs/operators';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {
    activateCharacterSuccess,
    addCharacterSuccess,
    createCampaignSuccess,
    deactivateCharacterSuccess,
    deleteCampaignSuccess,
    deleteCharacterSuccess,
    deletePlayerSuccess,
    leaveCampaign,
    notificationToken,
    revokeInviteSuccess,
    selectCurrentCampaign,
    updateEventSuccess,
    userLoad
} from 'gamesis-state';
import {Store} from '@ngrx/store';
import {Injectable} from '@angular/core';
import {NGXLogger} from 'ngx-logger';
import {NotificationProvider} from '../services/notification';
import {fromPromise} from 'rxjs/internal/observable/innerFrom';
import {Device} from '@capacitor/device';
import {SubscriptionsService} from 'gamesis-notification-client';

@Injectable()
export class NotificationEffect {

    userLoaded$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(userLoad),
                tap(_ => {
                    this.provider.init();
                })
            ), {dispatch: false});

    registerNotifications$ = createEffect(() =>
        this.actions$.pipe(
            ofType(notificationToken),
            combineLatestWith(
                this.store.select(selectCurrentCampaign),
                fromPromise(Device.getInfo())
            ),
            filter(([_, campaign]) => !!campaign),
            tap(([action, campaign, info]) => {
                // Register with Apple / Google to receive push via APNS/FCM
                this.logger.debug(`registering token ${action.token} to campaign ${campaign.name}`);
                const device = [info?.platform, info?.name, info?.operatingSystem, info?.model, info?.manufacturer]
                    .filter(value => value)
                    .map(value => value.trim())
                    .filter(value => value.length > 0)
                    .join(', ');
                this.subscriptionService.register(campaign.id, action.token, device).subscribe(() => {
                    this.logger.info('got device token registered');
                });
            })
        ), {dispatch: false}
    );

    campaignCreated$ = createEffect(() =>
        this.actions$.pipe(
            ofType(createCampaignSuccess),
            tap(_ => {
                this.provider.success('created new campaign');
            })
        ), {dispatch: false}
    );

    campaignDeleted$ = createEffect(() =>
        this.actions$.pipe(
            ofType(deleteCampaignSuccess),
            tap(_ => {
                this.provider.success('campaign deleted');
            })
        ), {dispatch: false}
    );

    characterDeleted$ = createEffect(() =>
        this.actions$.pipe(
            ofType(deleteCharacterSuccess),
            tap(_ => {
                this.provider.success('character deleted');
            })
        ), {dispatch: false}
    );

    characterCreated$ = createEffect(() =>
        this.actions$.pipe(
            ofType(addCharacterSuccess),
            tap(_ => {
                this.provider.success('character created');
            })
        ), {dispatch: false}
    );

    characterActivated$ = createEffect(() =>
        this.actions$.pipe(
            ofType(activateCharacterSuccess),
            tap(_ => {
                this.provider.success('character re activated');
            })
        ), {dispatch: false}
    );

    characterDeactivated$ = createEffect(() =>
        this.actions$.pipe(
            ofType(deactivateCharacterSuccess),
            tap(_ => {
                this.provider.success('character archived');
            })
        ), {dispatch: false}
    );

    playerRemoved$ = createEffect(() =>
        this.actions$.pipe(
            ofType(deletePlayerSuccess),
            tap(_ => {
                this.provider.success('player removed from campaign');
            })
        ), {dispatch: false}
    );

    inviteRevoked$ = createEffect(() =>
        this.actions$.pipe(
            ofType(revokeInviteSuccess),
            tap(_ => {
                this.provider.success('invite revoked');
            })
        ), {dispatch: false}
    );

    leftCampaign$ = createEffect(() =>
        this.actions$.pipe(
            ofType(leaveCampaign),
            tap(_ => {
                this.provider.success('left campaign');
            })
        ), {dispatch: false}
    );

    updateEvent = createEffect(() =>
        this.actions$.pipe(
            ofType(updateEventSuccess),
            tap(_ => {
                this.provider.success('Event updated');
            })
        ), {dispatch: false}
    );

    constructor(private actions$: Actions,
                private provider: NotificationProvider,
                private subscriptionService: SubscriptionsService,
                private store: Store,
                private logger: NGXLogger) {
    }
}
